import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    LsqApiServiceListSource,
    Pagination,
    SortDirectionFinancing
} from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    FastTrackRouteQueryParams,
    FeatureFlags
} from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    atlas,
    GROUPED_INVOICE_DETAIL,
    INVOICE_DETAIL,
    SUPPLY_CHAIN_DIRECT_SELLER_INVOICES,
    SUPPLY_CHAIN_DIRECT_SELLER_PARENT_GROUPED_INVOICE_DETAIL
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    GetSellerInvoiceExportRequest,
    InvoiceBuyerApprovalStatus,
    InvoiceExportType,
    InvoiceFundingStatus,
    InvoiceSummary,
    ListGroupedInvoiceSummaryForSellerRequest,
    ListInvoiceSummaryForSellerRequest
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { Guid } from '@lsq-frontend/remote-services/enterprise/common';
import {
    InvoiceFundingStatus2,
    InvoiceSummary2,
    ListGroupedInvoiceSummaryForSellerRequest2,
    ListGroupedInvoiceSummaryForSellerService,
    ListInvoiceSummaryForSellerRequest2,
    ListInvoiceSummaryForSellerResponse2,
    ListInvoiceSummaryForSellerService
} from '@lsq-frontend/remote-services/financing-graphql';
import {
    ExportType,
    GetSellerInvoiceExportService
} from '@lsq-frontend/remote-services/report-center';
import {
    ClassNames,
    Color,
    forLifeOf,
    getDynamicRouteLink,
    getDynamicRouterLink,
    Interaction,
    ISelectOption,
    LifecycleAware,
    StatusColor,
    Table,
    TableHelper,
    TabsConfig
} from '@lsq-frontend/common-ui';
import {
    FeatureFlagService,
    getRouterLinkById,
    getRouterLinkUrlById,
    Icons,
    LsqDatePipe,
    ResponsiveService,
    WindowReferenceService
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { isNil, omitBy } from 'lodash';
import { Observable } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    first,
    map,
    pluck,
    shareReplay,
    skip
} from 'rxjs/operators';
import { TAKE_ALL } from '../../../../constants';
import {
    InvoiceFundingStatusDisplay,
    InvoiceFundingStatusDotEnum
} from '../../../supply-chain-direct.models';
import { ScfSellerService } from '../../services/scf-seller.service';
import { InvoicesFilterType, LoadableInvoiceSummary } from '../invoices.helpers';

@LifecycleAware()
@Component({
    selector: 'supply-chain-invoices-list',
    templateUrl: './invoices-list.component.html',
    styleUrls: ['./invoices-list.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class InvoicesListComponent implements OnInit {
    public readonly nullValue = Table.NullAmount;
    public readonly classNames = ClassNames;
    public readonly icons = Icons;
    public readonly color = Color;
    public readonly statusColor = StatusColor;
    public readonly invoiceFundingStatus = InvoiceFundingStatus;
    public readonly invoiceFundingStatusDotEnum = InvoiceFundingStatusDotEnum;
    public readonly debounceTime = Interaction.SearchDebounceTimeMs;
    public readonly featureFlags = FeatureFlags;

    public readonly getRowClassNameBound = this._getRowClassName.bind(this);
    public readonly openInvoiceDetailsBound = this.openInvoiceDetails.bind(this);
    public readonly convertExportRequestBound = this.convertExportRequest.bind(this);
    public readonly isInvoiceClickableBound = this.isInvoiceClickable.bind(this);

    public readonly expandedGroupInvoiceKeys = new Set<string>();
    public buyerPaidEarlyPaymentFeePercentageVisible$: Observable<boolean>;
    public readonly tabsConfig: TabsConfig[] = [
        {
            label: this._i18n('Invoices'),
            routerLink: getDynamicRouteLink(
                getRouterLinkUrlById(atlas, SUPPLY_CHAIN_DIRECT_SELLER_INVOICES),
                {}
            ),
            queryParamHandling: 'merge',
            queryParams: {
                tab: FastTrackRouteQueryParams.InvoicesTab
            }
        },
        {
            label: this._i18n('Pending Credit Memos'),
            routerLink: getDynamicRouteLink(
                getRouterLinkUrlById(atlas, SUPPLY_CHAIN_DIRECT_SELLER_INVOICES),
                {}
            ),
            queryParams: {
                tab: FastTrackRouteQueryParams.PendingCreditMemosTab
            },
            icon: Icons.QuestionCircleLight,
            iconPopoverText: this._i18n(
                'This tab displays credit memos (credit notes) that have been issued to your buyer. Credit memos are held here and grouped with new incoming invoices until the net total reaches a positive amount. Once the grouped invoices reach a positive total amount, they will be  moved to the invoices tab.'
            ),
            iconSize: 14,
            iconColor: Color.Slate
        }
    ];
    public activeTabLabel$ = this._activatedRoute.queryParams.pipe(
        map((queryParams) => queryParams.tab)
    );
    public routeQueryParams = FastTrackRouteQueryParams;

    public reportName = `Invoices_${this._lsqDatePipe
        .transform(new Date(), 'shortDate')
        .replace(/\//g, '-')}`;

    public readonly processingInvoiceStatus = this._i18n({
        value: 'Processing'
    });
    public readonly invoiceNumberLabel = this._i18n({
        value: 'Invoice #',
        meaning: 'Invoice # column header'
    });
    public readonly statusHeader = this._i18n({
        value: 'Status',
        meaning: 'Status column header'
    });
    public readonly buyerHeader = this._i18n({
        value: 'Buyer'
    });
    public readonly invoiceDateHeader = this._i18n({
        value: 'Invoice Date'
    });
    public readonly invoiceDueDateHeader = this._i18n({
        value: 'Due Date'
    });
    public readonly invoiceNumberHeader = this._i18n({
        value: 'Invoice Number',
        meaning: 'Invoice # column header'
    });
    public readonly amountHeader = this._i18n({
        value: 'Invoice Amount',
        meaning: 'Amount column header'
    });
    public readonly buyerDiscountHeader = this._i18n({
        value: 'Buyer Discount',
        meaning: 'Buyer Discount column header'
    });
    public readonly buyerDiscountPercentageHeader = this._i18n({
        value: 'Buyer Fee Percentage',
        meaning: 'column header'
    });
    public readonly supplierDiscountHeader = this._i18n({
        value: 'Supplier Discount',
        meaning: 'Supplier Discount column header'
    });
    public readonly dueHeader = this._i18n({
        value: 'Due In',
        meaning: 'Due column header'
    });
    public readonly invoiceNumberTruncateAfter$ = this.windowReferenceService.resizeStream.pipe(
        map((dimensions) => {
            if (dimensions.width > 1625) {
                return 20;
            }

            if (dimensions.width > 1270) {
                return 15;
            }
            return 10;
        })
    );
    public readonly invoiceFundingStatusModifications = InvoiceFundingStatusModifications;
    public readonly approvalStatuses = [
        InvoiceBuyerApprovalStatus.APPROVED,
        InvoiceBuyerApprovalStatus.REJECTED
    ];

    public isKillSwitchEngaged$: Observable<boolean>;
    public filterOptions: ISelectOption[];
    public filtersFormGroup = new FormGroup({
        invoiceFilter: new FormControl(InvoicesFilterType.ShowAll),
        invoiceNumber: new FormControl('')
    });
    public tableDataSource: LsqApiServiceListSource<
        ListInvoiceSummaryForSellerResponse2,
        ListInvoiceSummaryForSellerRequest2
    >;
    public filterChange$: Observable<InvoicesFilterType>;
    public invoiceNumberFilterChange$: Observable<string>;

    constructor(
        public invoiceFundingStatusDisplay: InvoiceFundingStatusDisplay,
        public responsiveService: ResponsiveService,
        public windowReferenceService: WindowReferenceService,
        public getSellerInvoiceExportService: GetSellerInvoiceExportService,
        private _scfSellerService: ScfSellerService,
        private _listInvoiceSummaryForSellerService: ListInvoiceSummaryForSellerService,
        private _listGroupedInvoiceSummaryService: ListGroupedInvoiceSummaryForSellerService,
        private _userService: UserService,
        private _i18n: I18n,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _lsqDatePipe: LsqDatePipe,
        private _featureFlagService: FeatureFlagService
    ) {
        this._activatedRoute.queryParams.subscribe((queryParams) => {
            if (
                queryParams.tab === FastTrackRouteQueryParams.InvoicesTab &&
                Object.keys(queryParams).length === 1
            ) {
                this.setFilterQueryParams();
            }
        });
    }

    public ngOnInit(): void {
        this.isKillSwitchEngaged$ = this._scfSellerService.isKillSwitchEngaged$;
        this._featureFlagService.isOn(FeatureFlags.PayLater).subscribe((isGreenfield) => {
            this.filterOptions = [
                {
                    label: this._i18n('All Invoices'),
                    selectedLabel: this._i18n('All Invoices'),
                    value: InvoicesFilterType.ShowAll
                },
                {
                    label: this._i18n('Eligible'),
                    selectedLabel: this._i18n('Eligible Invoices'),
                    value: InvoicesFilterType.OnlyAvailable
                },
                ...(isGreenfield
                    ? [
                          {
                              label: this._i18n('Early Payment Scheduled'),
                              selectedLabel: this._i18n('Early Payment Scheduled'),
                              value: InvoicesFilterType.OnlyScheduled
                          }
                      ]
                    : []),
                {
                    label: this._i18n('Pending'),
                    selectedLabel: this._i18n('Pending Invoices'),
                    value: InvoicesFilterType.OnlyPending
                },
                {
                    label: this._i18n('Early Paid'),
                    selectedLabel: this._i18n('Early Paid Invoices'),
                    value: InvoicesFilterType.OnlyPaid
                },
                {
                    label: this._i18n('Ineligible'),
                    selectedLabel: this._i18n('Ineligible Invoices'),
                    value: InvoicesFilterType.OnlyExpired
                },
                {
                    label: this._i18n('Paid'),
                    selectedLabel: this._i18n('Paid Invoices'),
                    value: InvoicesFilterType.OnlyPaidOnTerms
                }
            ];
        });

        const queryParamMap = this._activatedRoute.snapshot.queryParamMap;
        const invoicesFilter: string =
            InvoicesFilterType[queryParamMap.get(FastTrackRouteQueryParams.InvoiceTypeFilter)];
        const invoiceNumber = queryParamMap.get(FastTrackRouteQueryParams.SearchText);

        if (invoicesFilter || invoiceNumber) {
            this.filtersFormGroup.patchValue(
                {
                    invoiceFilter: InvoicesFilterType[invoicesFilter],
                    invoiceNumber: invoiceNumber
                },
                {
                    onlySelf: true,
                    emitEvent: false
                }
            );
        }

        this.tableDataSource = new LsqApiServiceListSource(
            this._listInvoiceSummaryForSellerService
        );
        this.tableDataSource.data$.subscribe(() => {
            this._activatedRoute.queryParams.pipe(first()).subscribe((queryParams) => {
                if (
                    !Object.keys(queryParams).length ||
                    (queryParams.tab === FastTrackRouteQueryParams.InvoicesTab &&
                        Object.keys(queryParams).length === 1)
                ) {
                    this.setFilterQueryParams();
                }
            });
        });
        this._listInvoices(
            TableHelper.getPaginationByQueryParams(this._activatedRoute.snapshot.queryParamMap),
            InvoicesFilterType[invoicesFilter],
            invoiceNumber
        );

        this.filterChange$ = this.filtersFormGroup.controls.invoiceFilter.valueChanges.pipe(
            distinctUntilChanged(),
            filter((filterValue) => typeof filterValue === 'number'),
            shareReplay(1)
        );

        this.invoiceNumberFilterChange$ =
            this.filtersFormGroup.controls.invoiceNumber.valueChanges.pipe(
                distinctUntilChanged(),
                debounceTime(this.debounceTime),
                shareReplay(1)
            );

        this.filterChange$.pipe(forLifeOf(this)).subscribe((invoicesFilter) => {
            const invoiceNumber = this.filtersFormGroup.controls.invoiceNumber.value;
            this._listInvoices({ skip: 0 }, invoicesFilter, invoiceNumber);
        });

        this.invoiceNumberFilterChange$
            .pipe(forLifeOf(this), distinctUntilChanged(), shareReplay(1))
            .subscribe((invoiceNumberFilter) => {
                this.expandedGroupInvoiceKeys.clear();
                const invoicesFilterType = this.filtersFormGroup.controls.invoiceFilter.value;
                this._listInvoices({ skip: 0 }, invoicesFilterType, invoiceNumberFilter);
            });

        this.invoiceNumberFilterChange$.subscribe(() => {
            this.tableDataSource.rows$
                .pipe(
                    forLifeOf(this),
                    skip(1),
                    first(),
                    filter((invoiceRows) =>
                        invoiceRows.some((invoiceRow) => !!invoiceRow.groupedInvoiceNumberMatched)
                    )
                )
                .subscribe(async (invoiceRows) => {
                    const filteredRows = invoiceRows.filter(
                        (invoiceRow) => invoiceRow.groupedInvoiceNumberMatched
                    );
                    for (const row of filteredRows) {
                        await this._expandGroupByInvoiceKey(row.refInvoiceKey);
                    }
                });
        });

        this.isKillSwitchEngaged$.subscribe();

        this.buyerPaidEarlyPaymentFeePercentageVisible$ = this.tableDataSource.data$.pipe(
            pluck('buyerPaidEarlyPaymentFeePercentageVisible'),
            forLifeOf(this),
            shareReplay(1)
        );
    }

    private _getSellerKey(): string {
        return this._userService.getAlternateCompanyKey();
    }

    public async handleInvoiceGroupToggleClick(row: InvoiceSummary): Promise<void> {
        if (this.expandedGroupInvoiceKeys.has(row.refInvoiceKey)) {
            await this._collapseGroup(row.refInvoiceKey);
        } else {
            await this._expandGroupByInvoiceKey(row.refInvoiceKey);
        }
    }

    private async _collapseGroup(refInvoiceKey: string): Promise<void> {
        this.expandedGroupInvoiceKeys.delete(refInvoiceKey);
        await this.tableDataSource.collapseGroup('refInvoiceKey', refInvoiceKey);
    }

    private async _expandGroupByInvoiceKey(refInvoiceKey: string): Promise<void> {
        this.expandedGroupInvoiceKeys.add(refInvoiceKey);
        let invoiceSummaryRows = await this.tableDataSource.rows$.pipe(first()).toPromise();
        let index = invoiceSummaryRows.findIndex((row) => row.refInvoiceKey === refInvoiceKey);
        if (index < 0) {
            return;
        }

        const loadingRow = new LoadableInvoiceSummary({
            refInvoiceKey: refInvoiceKey,
            refGroupedInvoiceKey: 'loading' // So we get the grouped invoice styling
        });
        loadingRow.isRowLoading = true;
        this.tableDataSource.expandGroup(index, [loadingRow]);

        const { rows: groupedInvoices } = await this._listGroupedInvoiceSummaryService.executeOnce(
            new ListGroupedInvoiceSummaryForSellerRequest2({
                refInvoiceKey,
                refSellerKey: this._getSellerKey(),
                pagination: {
                    skip: 0,
                    take: TAKE_ALL,
                    sortColumn: 'invoiceDate',
                    sortDirection: SortDirectionFinancing.DESCENDING,
                    // TODO(dmayer): Make these fields optional in the proto.
                    secondarySortColumn: '',
                    secondarySortDirection: null,
                    tertiarySortColumn: '',
                    tertiarySortDirection: null
                },
                includePendingPayables: true
            })
        );
        await this.tableDataSource.collapseGroup('refInvoiceKey', refInvoiceKey);
        invoiceSummaryRows = await this.tableDataSource.rows$.pipe(first()).toPromise();
        index = invoiceSummaryRows.findIndex((row) => row.refInvoiceKey === refInvoiceKey);

        if (index < 0) {
            return;
        }

        this.tableDataSource.expandGroup(
            index,
            groupedInvoices.map((invoice) => new LoadableInvoiceSummary(invoice))
        );
    }

    private async _listInvoices(
        pagination: Partial<Pagination> = { skip: 0, take: 25 },
        invoicesFilter: InvoicesFilterType = InvoicesFilterType.ShowAll,
        invoiceNumber?: string
    ): Promise<void> {
        const currentSkip = this.tableDataSource.skip;
        const currentTake = this.tableDataSource.take;
        const paginationRequest = {
            sortColumn: 'fundingStatus',
            sortDirection: SortDirectionFinancing.ASCENDING,
            secondarySortColumn: '',
            secondarySortDirection: null,
            skip: currentSkip,
            take: currentTake,
            ...omitBy(pagination, isNil)
        } as Pagination;

        const fundingStatuses = this._getFundingStatusFromFilter(invoicesFilter);

        this.tableDataSource.dispatchRequest(
            new ListInvoiceSummaryForSellerRequest2({
                approvalStatus: this.approvalStatuses,
                fundingStatus: fundingStatuses,
                refSellerKey: this._getSellerKey(),
                useMapStatus: true,
                pagination: paginationRequest,
                invoiceNumber,
                includePendingPayables: true
            })
        );
    }

    private _getFundingStatusFromFilter(
        invoicesFilter: InvoicesFilterType = InvoicesFilterType.ShowAll
    ): InvoiceFundingStatus[] {
        let fundingStatuses: InvoiceFundingStatus[];

        if (invoicesFilter === InvoicesFilterType.OnlyAvailable) {
            fundingStatuses = [InvoiceFundingStatus.UNFUNDED];
        }
        if (invoicesFilter === InvoicesFilterType.OnlyExpired) {
            fundingStatuses = [InvoiceFundingStatus.INELIGIBLE, InvoiceFundingStatus.WILL_NOT_FUND];
        }
        if (invoicesFilter === InvoicesFilterType.OnlyPaid) {
            fundingStatuses = [InvoiceFundingStatus.FUNDED, InvoiceFundingStatus.EARLY_PAID_CLOSED];
        }
        if (invoicesFilter === InvoicesFilterType.OnlyPending) {
            fundingStatuses = [InvoiceFundingStatus.PENDING];
        }
        if (invoicesFilter === InvoicesFilterType.OnlyClosed) {
            fundingStatuses = [InvoiceFundingStatus.CLOSED, InvoiceFundingStatus.EARLY_PAID_CLOSED];
        }
        if (invoicesFilter === InvoicesFilterType.OnlyPaidOnTerms) {
            fundingStatuses = [InvoiceFundingStatus.PAID, InvoiceFundingStatus.CLOSED];
        }
        if (invoicesFilter === InvoicesFilterType.OnlyScheduled) {
            fundingStatuses = [InvoiceFundingStatus.SCHEDULED];
        }
        return fundingStatuses;
    }

    public async openInvoiceDetails(invoice: LoadableInvoiceSummary): Promise<void> {
        // Set the filters right before we navigate to make sure the url is correct.
        // This protects against cases like clicking the navigation item for this page.
        await this.setFilterQueryParams();
        if (invoice.isGroup) {
            await this._router.navigateByUrl(
                this._router.createUrlTree(
                    getDynamicRouterLink(
                        getRouterLinkById(
                            atlas,
                            SUPPLY_CHAIN_DIRECT_SELLER_PARENT_GROUPED_INVOICE_DETAIL,
                            {
                                noStandaloneLeadingSlash: true
                            }
                        ),
                        {
                            invoiceKey: invoice.refInvoiceKey
                        }
                    ),
                    {
                        queryParams: {
                            [FastTrackRouteQueryParams.PreviousRouteId]:
                                SUPPLY_CHAIN_DIRECT_SELLER_INVOICES
                        }
                    }
                )
            );
        } else if (invoice.refGroupedInvoiceKey) {
            const isCreditMemo =
                InvoiceFundingStatusModifications.CREDIT_MEMO === this.getInvoiceStatus(invoice);
            const groupedInvoiceType = isCreditMemo ? 'creditMemo' : 'approval';
            await this._router.navigateByUrl(
                this._router.createUrlTree(
                    getDynamicRouterLink(
                        getRouterLinkById(atlas, GROUPED_INVOICE_DETAIL, {
                            noStandaloneLeadingSlash: true
                        }),
                        { groupedInvoiceKey: invoice.refGroupedInvoiceKey }
                    ),
                    {
                        queryParams: {
                            [FastTrackRouteQueryParams.PreviousRouteId]:
                                SUPPLY_CHAIN_DIRECT_SELLER_INVOICES,
                            [FastTrackRouteQueryParams.GroupedInvoiceType]: groupedInvoiceType
                        }
                    }
                )
            );
        } else {
            await this._router.navigateByUrl(
                this._router.createUrlTree(
                    getDynamicRouterLink(
                        getRouterLinkById(atlas, INVOICE_DETAIL, {
                            noStandaloneLeadingSlash: true
                        }),
                        { invoiceKey: invoice.refInvoiceKey }
                    ),
                    {
                        queryParams: {
                            [FastTrackRouteQueryParams.PreviousRouteId]:
                                SUPPLY_CHAIN_DIRECT_SELLER_INVOICES
                        }
                    }
                )
            );
        }
    }

    public async setFilterQueryParams(): Promise<void> {
        const queryParams = {
            ...TableHelper.getQueryParamsByListSource(this.tableDataSource),
            [FastTrackRouteQueryParams.InvoiceTypeFilter]:
                this.filtersFormGroup.controls.invoiceFilter.value || undefined,
            [FastTrackRouteQueryParams.SearchText]:
                this.filtersFormGroup.controls.invoiceNumber.value || undefined,
            tab: FastTrackRouteQueryParams.InvoicesTab
        };
        await this._router.navigate([], {
            relativeTo: this._activatedRoute,
            queryParams: queryParams,
            replaceUrl: true
        });
    }

    private _getRowClassName(row: InvoiceSummary): string {
        const classList = [];
        if (row.isGroup) {
            classList.push('is-group');

            if (this.expandedGroupInvoiceKeys.has(row.refInvoiceKey)) {
                classList.push('expanded');
            }
        }
        if (row.refGroupedInvoiceKey) {
            classList.push('is-member-of-group');
        }
        return classList.join(' ');
    }

    public trackByInvoiceKey(_index: number, { refInvoiceKey }: InvoiceSummary): Guid {
        return refInvoiceKey;
    }

    public isInvoiceClickable(row: InvoiceSummary): boolean {
        return (
            !row.isGroup ||
            this._featureFlagService.isOnSync(FeatureFlags.PendingCreditMemosDetails)
        );
    }

    public getInvoiceStatus(
        row: InvoiceSummary | InvoiceSummary2
    ): InvoiceFundingStatus | InvoiceFundingStatus2 | InvoiceFundingStatusModifications {
        if (
            row.finalApprovedAmount.cents < 0 &&
            !row.isGroup &&
            row.buyerApprovalStatus !== InvoiceBuyerApprovalStatus.REJECTED
        ) {
            return this.invoiceFundingStatusModifications.CREDIT_MEMO;
        } else {
            return row.invoiceStatus ?? row.fundingStatus;
        }
    }

    public convertExportRequest(
        request: ListInvoiceSummaryForSellerRequest
    ): GetSellerInvoiceExportRequest {
        return {
            invoiceExportType: InvoiceExportType.INVOICE,
            useMapStatus: true,
            ...request
        };
    }
}

export enum InvoiceFundingStatusModifications {
    ADJUSTMENT = 'ADJUSTMENT',
    CREDIT_MEMO = 'CREDIT_MEMO'
}
